import type { QueryClient } from '@tanstack/react-query';
import type { Address } from 'viem';

// Central registry of tanstack-query keys. Consumers build keys through
// these factories instead of inlining tuples, so a rename or signature
// change breaks the type check at every call site at once — and the
// invalidation cascade below can never target a key shape no consumer uses.
// The profile's three predictions scopes: its sub-tab, and the key axis of
// the per-scope forecasts query.
export type PredictionsScope = 'active' | 'closed' | 'orders';

export const queryKeys: Readonly<{
  marketPredictions: (
    signalId: string,
  ) => readonly ['market-all-predictions', string];
  marketPredictionsAll: () => readonly ['market-all-predictions'];
  issuerMarketPredictions: (
    marketId: string,
    issuerId: string,
  ) => readonly ['market-issuer-predictions', string, string];
  issuerMarketPredictionsAll: () => readonly ['market-issuer-predictions'];
  // Keyed on the WHOLE target: every field is a fetch input (the window is
  // computed from the caller's anchor timestamps, the series from
  // slug/asset/quote), so two consumers charting the same market from
  // different anchors must not share one cache entry.
  marketChart: (target: {
    id: string;
    asset: string;
    slug?: string | null | undefined;
    quoteAsset: string;
    createdAt: string;
    resolvesAt: string;
  }) => readonly [
    'market-chart',
    {
      id: string;
      asset: string;
      slug?: string | null | undefined;
      quoteAsset: string;
      createdAt: string;
      resolvesAt: string;
    },
  ];
  marketSignal: (id: string) => readonly ['market-signals', 'single', string];
  marketSignalShareRef: (
    ref: string,
  ) => readonly ['market-signals', 'share-ref', string];
  marketSignalsStatus: () => readonly ['market-signals', 'status'];
  marketSignalsNewCount: (
    since: string,
    issuerId: string | undefined,
  ) => readonly ['market-signals', 'new-count', string, string | undefined];
  marketSignalsNewCountAll: () => readonly ['market-signals', 'new-count'];
  marketSignalsFilters: () => readonly ['market-signals', 'filters'];
  marketSignalsList: (
    status: string | null,
    assets: readonly string[],
    usernames: readonly string[],
    participated: string | null,
    notParticipated: string | null,
    sort: string,
  ) => readonly [
    'market-signals',
    'list',
    string | null,
    readonly string[],
    readonly string[],
    string | null,
    string | null,
    string,
  ];
  marketSignalsListAll: () => readonly ['market-signals', 'list'];
  marketSignalsCount: (
    status: string | null,
    assets: readonly string[],
    usernames: readonly string[],
    participated: string | null,
    notParticipated: string | null,
  ) => readonly [
    'market-signals',
    'count',
    string | null,
    readonly string[],
    readonly string[],
    string | null,
    string | null,
  ];
  marketForecastsProfile: (
    issuerId: string | undefined,
    scope: PredictionsScope,
  ) => readonly [
    'market-forecasts-profile',
    string | undefined,
    PredictionsScope,
  ];
  marketForecastsProfileAll: () => readonly ['market-forecasts-profile'];
  myPredictions: (
    issuerId: string | undefined,
  ) => readonly ['my-predictions', string | undefined];
  myPredictionsAll: () => readonly ['my-predictions'];
  performanceChart: (
    issuerId: string | undefined,
  ) => readonly ['performance-chart', string | undefined];
  performanceChartAll: () => readonly ['performance-chart'];
  predictionsBySymbol: (
    issuerId: string | undefined,
  ) => readonly ['predictions-by-symbol', string | undefined];
  predictionsBySymbolAll: () => readonly ['predictions-by-symbol'];
  livePrice: (
    slug: string,
    quoteAsset: string,
  ) => readonly ['live-price', string, string];
  ohlc: (
    ticker: string,
    interval: string,
    from: string,
    to: string,
  ) => readonly ['ohlc', string, string, string, string];
  assetName: (slug: string) => readonly ['assetName', string];
  userCheck: (username: string) => readonly ['user-check', string];
  userInfo: (username: string) => readonly ['user-info', string];
  userInfoAll: () => readonly ['user-info'];
  sanrIssuerByAddress: (
    address: string,
  ) => readonly ['sanr-issuer-by-address', string];
  // ENS name or address → username resolution.
  wildcardResolve: (
    path: string | undefined,
  ) => readonly ['wildcard-resolve', string | undefined];
  resolvedIssuers: (
    addresses: string[],
  ) => readonly ['sanr-issuers-resolve', string[]];
  userSettings: () => readonly ['user-settings'];
  tgBotLink: (username: string) => readonly ['tg-bot-link', string];
  withdrawBalance: (
    smartType: string,
    userAddress: Address,
  ) => readonly ['withdraw-balance', string, Address];
  sellPrice: (
    issuerAddress: Address,
    supply: number,
    balance: number,
  ) => readonly ['sell-price', Address, number, number];

  // The arena candidate listings (/v1/issuers through the issuers-api
  // filters) and the caller's own pinned rank within one — every key under
  // the issuers root reads active_stake_on_user, so the stake tx cascade
  // targets the whole root. fundedAddresses — the allocated wallets a
  // listing names or excludes — is a fetch input, hence a key segment: a
  // new allocation refetches the listings instead of leaving a stale set.
  issuersListing: (
    status: string,
    search: string,
    sortBy: string,
    sortDir: string,
    page: number,
    fundedAddresses: readonly string[],
  ) => readonly [
    'issuers',
    string,
    string,
    string,
    string,
    number,
    readonly string[],
  ];
  issuersTopStakedEligible: (
    fundedAddresses: readonly string[],
  ) => readonly ['issuers', 'top-staked-eligible', readonly string[]];
  issuersAll: () => readonly ['issuers'];
  issuersEligibleCount: (
    search: string,
    fundedAddresses: readonly string[],
  ) => readonly ['issuers-count', 'eligible', string, readonly string[]];
  myPosition: (
    issuerId: string | undefined,
    status: string,
    search: string,
    sortBy: string,
    sortDir: string,
    volume: string | undefined,
    fundedAddresses: readonly string[],
  ) => readonly [
    'my-position',
    string | undefined,
    string,
    string,
    string,
    string,
    string | undefined,
    readonly string[],
  ];
  myPositionAll: () => readonly ['my-position'];

  // The arena-api families. The issuer row is keyed by username (the
  // SSO-safe identifier) or by address under ONE 'issuer' family, with a
  // discriminator segment so the two lookups never share a cache entry; the
  // family root is what both cascades target.
  arenaAllocations: () => readonly ['arena', 'allocations'];
  arenaStrategyIssuers: () => readonly ['arena', 'strategy-issuers'];
  arenaIssuer: (
    username: string,
  ) => readonly ['arena', 'issuer', 'by-username', string];
  arenaIssuerByAddress: (
    address: string,
  ) => readonly ['arena', 'issuer', 'by-address', string];
  arenaIssuerAll: () => readonly ['arena', 'issuer'];
  arenaMetrics: (allocationId: string) => readonly ['arena', 'metrics', string];
  arenaPositions: (
    allocationId: string,
  ) => readonly ['arena', 'positions', string];
  arenaMarkets: (allocationId: string) => readonly ['arena', 'markets', string];
  arenaWeekly: (allocationId: string) => readonly ['arena', 'weekly', string];
  arenaTrades: (
    allocationId: string,
    asset: string | undefined,
  ) => readonly ['arena', 'trades', string, string | undefined];
  arenaEquityCurve: (
    allocationId: string,
  ) => readonly ['arena', 'equity-curve', string];
  arenaEquityCurves: (
    allocationIds: string[],
  ) => readonly ['arena', 'equity-curves', string[]];
  arenaStakers: (
    providerAddress: string,
  ) => readonly ['arena', 'stakers', string];
  arenaStakersAll: () => readonly ['arena', 'stakers'];
  arenaPortfolioStakes: (
    stakerAddress: string,
  ) => readonly ['arena', 'portfolio-stakes', string];
  arenaPortfolioStakesAll: () => readonly ['arena', 'portfolio-stakes'];
  arenaStakeUsers: (
    addresses: string[],
  ) => readonly ['arena', 'stake-users', string[]];
  arenaStakeUsersAll: () => readonly ['arena', 'stake-users'];
  arenaStakeUser: (address: string) => readonly ['arena', 'stake-user', string];
  arenaStakeUserAll: () => readonly ['arena', 'stake-user'];
  arenaIssuersByAddresses: (
    addresses: string[],
  ) => readonly ['arena', 'issuers-by-address', string[]];
  arenaIssuersByAddressesAll: () => readonly ['arena', 'issuers-by-address'];

  // The paper-strategy family (strategies-api): every key sits under one
  // root, the target of the strategy cascade below. The pairs list sits
  // there too: an order moves nothing in it, but its last prices are what
  // the book is marked at, so the refetch is wanted.
  strategiesAll: () => readonly ['strategies'];
  strategiesByUser: (
    userId: string,
  ) => readonly ['strategies', 'by-user', string];
  strategyBook: (strategyId: string) => readonly ['strategies', 'book', string];
  strategyOrders: (
    strategyId: string,
  ) => readonly ['strategies', 'orders', string];
  strategyFills: (
    strategyId: string,
  ) => readonly ['strategies', 'fills', string];
  strategyTrades: (
    strategyId: string,
  ) => readonly ['strategies', 'trades', string];
  strategyEquity: (
    strategyId: string,
  ) => readonly ['strategies', 'equity', string];
  strategyStakes: (
    strategyIds: string[],
  ) => readonly ['strategies', 'stakes', string[]];
  tradablePairs: () => readonly ['strategies', 'pairs'];

  // The market activity panel's public reads — the whale exports. No
  // mutation touches them — nothing cascades here.
  traderDatasets: () => readonly ['traders', 'datasets'];
  traders: (
    asset: string,
    dataset: string,
  ) => readonly ['traders', 'list', string, string];
  traderTrades: (
    address: string,
    asset: string,
    dataset: string,
    direction: string,
  ) => readonly ['traders', 'trades', string, string, string, string];
  // The whale section's 24h AI summary, one generation per asset and
  // window end — a paid agent run, so the entry is held for the session.
  whaleSummary: (
    asset: string,
    to: string,
  ) => readonly ['traders', 'summary', string, string];
}> = {
  marketPredictions: (signalId: string) =>
    ['market-all-predictions', signalId] as const,
  marketPredictionsAll: () => ['market-all-predictions'] as const,
  issuerMarketPredictions: (marketId: string, issuerId: string) =>
    ['market-issuer-predictions', marketId, issuerId] as const,
  issuerMarketPredictionsAll: () => ['market-issuer-predictions'] as const,
  marketChart: (target) => ['market-chart', target] as const,
  marketSignal: (id: string) => ['market-signals', 'single', id] as const,
  marketSignalShareRef: (ref: string) =>
    ['market-signals', 'share-ref', ref] as const,
  marketSignalsStatus: () => ['market-signals', 'status'] as const,
  marketSignalsNewCount: (since: string, issuerId: string | undefined) =>
    ['market-signals', 'new-count', since, issuerId] as const,
  marketSignalsNewCountAll: () => ['market-signals', 'new-count'] as const,
  marketSignalsFilters: () => ['market-signals', 'filters'] as const,
  marketSignalsList: (
    status: string | null,
    assets: readonly string[],
    usernames: readonly string[],
    participated: string | null,
    notParticipated: string | null,
    sort: string,
  ) =>
    [
      'market-signals',
      'list',
      status,
      assets,
      usernames,
      participated,
      notParticipated,
      sort,
    ] as const,
  marketSignalsListAll: () => ['market-signals', 'list'] as const,
  marketSignalsCount: (
    status: string | null,
    assets: readonly string[],
    usernames: readonly string[],
    participated: string | null,
    notParticipated: string | null,
  ) =>
    [
      'market-signals',
      'count',
      status,
      assets,
      usernames,
      participated,
      notParticipated,
    ] as const,
  marketForecastsProfile: (
    issuerId: string | undefined,
    scope: PredictionsScope,
  ) => ['market-forecasts-profile', issuerId, scope] as const,
  marketForecastsProfileAll: () => ['market-forecasts-profile'] as const,
  myPredictions: (issuerId: string | undefined) =>
    ['my-predictions', issuerId] as const,
  myPredictionsAll: () => ['my-predictions'] as const,
  performanceChart: (issuerId: string | undefined) =>
    ['performance-chart', issuerId] as const,
  performanceChartAll: () => ['performance-chart'] as const,
  predictionsBySymbol: (issuerId: string | undefined) =>
    ['predictions-by-symbol', issuerId] as const,
  predictionsBySymbolAll: () => ['predictions-by-symbol'] as const,
  livePrice: (slug: string, quoteAsset: string) =>
    ['live-price', slug, quoteAsset] as const,
  ohlc: (ticker: string, interval: string, from: string, to: string) =>
    ['ohlc', ticker, interval, from, to] as const,
  assetName: (slug: string) => ['assetName', slug] as const,

  userCheck: (username: string) => ['user-check', username] as const,
  userInfo: (username: string) => ['user-info', username] as const,
  userInfoAll: () => ['user-info'] as const,
  sanrIssuerByAddress: (address: string) =>
    ['sanr-issuer-by-address', address] as const,
  wildcardResolve: (path: string | undefined) =>
    ['wildcard-resolve', path] as const,
  resolvedIssuers: (addresses: string[]) =>
    ['sanr-issuers-resolve', addresses] as const,
  userSettings: () => ['user-settings'] as const,
  tgBotLink: (username: string) => ['tg-bot-link', username] as const,

  withdrawBalance: (smartType: string, userAddress: Address) =>
    ['withdraw-balance', smartType, userAddress] as const,
  sellPrice: (issuerAddress: Address, supply: number, balance: number) =>
    ['sell-price', issuerAddress, supply, balance] as const,

  issuersListing: (
    status: string,
    search: string,
    sortBy: string,
    sortDir: string,
    page: number,
    fundedAddresses: readonly string[],
  ) =>
    [
      'issuers',
      status,
      search,
      sortBy,
      sortDir,
      page,
      fundedAddresses,
    ] as const,
  issuersTopStakedEligible: (fundedAddresses: readonly string[]) =>
    ['issuers', 'top-staked-eligible', fundedAddresses] as const,
  issuersAll: () => ['issuers'] as const,
  issuersEligibleCount: (search: string, fundedAddresses: readonly string[]) =>
    ['issuers-count', 'eligible', search, fundedAddresses] as const,
  myPosition: (
    issuerId: string | undefined,
    status: string,
    search: string,
    sortBy: string,
    sortDir: string,
    volume: string | undefined,
    fundedAddresses: readonly string[],
  ) =>
    [
      'my-position',
      issuerId,
      status,
      search,
      sortBy,
      sortDir,
      volume,
      fundedAddresses,
    ] as const,
  myPositionAll: () => ['my-position'] as const,

  arenaAllocations: () => ['arena', 'allocations'] as const,
  arenaStrategyIssuers: () => ['arena', 'strategy-issuers'] as const,
  arenaIssuer: (username: string) =>
    ['arena', 'issuer', 'by-username', username] as const,
  arenaIssuerByAddress: (address: string) =>
    ['arena', 'issuer', 'by-address', address] as const,
  arenaIssuerAll: () => ['arena', 'issuer'] as const,
  arenaMetrics: (allocationId: string) =>
    ['arena', 'metrics', allocationId] as const,
  arenaPositions: (allocationId: string) =>
    ['arena', 'positions', allocationId] as const,
  arenaMarkets: (allocationId: string) =>
    ['arena', 'markets', allocationId] as const,
  arenaWeekly: (allocationId: string) =>
    ['arena', 'weekly', allocationId] as const,
  arenaTrades: (allocationId: string, asset: string | undefined) =>
    ['arena', 'trades', allocationId, asset] as const,
  arenaEquityCurve: (allocationId: string) =>
    ['arena', 'equity-curve', allocationId] as const,
  arenaEquityCurves: (allocationIds: string[]) =>
    ['arena', 'equity-curves', allocationIds] as const,
  arenaStakers: (providerAddress: string) =>
    ['arena', 'stakers', providerAddress] as const,
  arenaStakersAll: () => ['arena', 'stakers'] as const,
  arenaPortfolioStakes: (stakerAddress: string) =>
    ['arena', 'portfolio-stakes', stakerAddress] as const,
  arenaPortfolioStakesAll: () => ['arena', 'portfolio-stakes'] as const,
  arenaStakeUsers: (addresses: string[]) =>
    ['arena', 'stake-users', addresses] as const,
  arenaStakeUsersAll: () => ['arena', 'stake-users'] as const,
  arenaStakeUser: (address: string) =>
    ['arena', 'stake-user', address] as const,
  arenaStakeUserAll: () => ['arena', 'stake-user'] as const,
  arenaIssuersByAddresses: (addresses: string[]) =>
    ['arena', 'issuers-by-address', addresses] as const,
  arenaIssuersByAddressesAll: () => ['arena', 'issuers-by-address'] as const,

  strategiesAll: () => ['strategies'] as const,
  strategiesByUser: (userId: string) =>
    ['strategies', 'by-user', userId] as const,
  strategyBook: (strategyId: string) =>
    ['strategies', 'book', strategyId] as const,
  strategyOrders: (strategyId: string) =>
    ['strategies', 'orders', strategyId] as const,
  strategyFills: (strategyId: string) =>
    ['strategies', 'fills', strategyId] as const,
  strategyTrades: (strategyId: string) =>
    ['strategies', 'trades', strategyId] as const,
  strategyEquity: (strategyId: string) =>
    ['strategies', 'equity', strategyId] as const,
  strategyStakes: (strategyIds: string[]) =>
    ['strategies', 'stakes', strategyIds] as const,
  tradablePairs: () => ['strategies', 'pairs'] as const,

  traderDatasets: () => ['traders', 'datasets'] as const,
  traders: (asset: string, dataset: string) =>
    ['traders', 'list', asset, dataset] as const,
  traderTrades: (
    address: string,
    asset: string,
    dataset: string,
    direction: string,
  ) => ['traders', 'trades', address, asset, dataset, direction] as const,
  whaleSummary: (asset: string, to: string) =>
    ['traders', 'summary', asset, to] as const,
};

// Everything a prediction mutation (create / edit SL-TP-trigger / close /
// cancel) can make stale, invalidated as ONE shared cascade — every mutation
// call site must go through here, never a hand-rolled per-site list: per-site
// lists drift (one call site shipping a subset of its sibling's left a closed
// position rendering as an open call — a live bug). Every target is a root
// prefix so each parameterized child key matches. The arena issuer family is
// in because a prediction create moves the row's total_predictions, which
// the candidate listing's volume-group filter derives from while caching the
// row never-stale — this cascade is its only prediction-path refresh.
// Deliberately NOT invalidated: marketSignalsCount (the filter-tab counts'
// 30s staleness is a documented trade-off), marketSignal (the single-market
// row carries no prediction-derived field — consensus and panels on a market
// page read the prediction keys above), and marketChart / livePrice / ohlc
// (price series don't depend on predictions). Settles every invalidation
// (rejections included) before resolving; adding a prediction-dependent query
// means extending BOTH this cascade and its coverage test.
export function invalidatePredictionQueries(
  queryClient: QueryClient,
): Promise<void> {
  return Promise.allSettled(
    [
      queryKeys.myPredictionsAll(),
      queryKeys.marketPredictionsAll(),
      queryKeys.issuerMarketPredictionsAll(),
      queryKeys.marketForecastsProfileAll(),
      queryKeys.marketSignalsListAll(),
      queryKeys.marketSignalsNewCountAll(),
      queryKeys.performanceChartAll(),
      queryKeys.predictionsBySymbolAll(),
      queryKeys.userInfoAll(),
      queryKeys.arenaIssuerAll(),
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  ).then(() => undefined);
}

// Everything a strategy or paper-trading write (create a strategy, place or
// cancel an order, set a position's levels) can make stale: the whole
// strategies root — the user's list, the book, the orders, fills, trades,
// the equity curve, the staked SAN and the pairs — as ONE cascade every
// write goes through. Settles every invalidation (rejections included)
// before resolving.
export function invalidateStrategyQueries(
  queryClient: QueryClient,
): Promise<void> {
  return Promise.allSettled([
    queryClient.invalidateQueries({ queryKey: queryKeys.strategiesAll() }),
  ]).then(() => undefined);
}
